import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, MenuItem, Modal, Select, TextField, Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import api, { localFlipkartApi } from "../Api";
import ScanSummary from "./ScanSummary";
import NotScannedDialog from "./NotScannedDialog";
import { addDaysToDateString, getAppDateString } from "../utils/date";

/**
 * Pickup / Return — a phone-first view for the person standing at the handover.
 *
 * The Prints page is a wide grid of every account and every column; on a phone it
 * means hunting across a table while a courier waits. Here you pick a group, an
 * account and which handover it is, and the page shows ONLY the two counts that
 * matter for that handover plus its OTC button. Everything fits one screen.
 */

const GROUPS = [
  { key: "MY", label: "MY (MYE + MYS)", prefixes: ["MYE ", "MYS "] },
  { key: "MYE", label: "MYE", prefixes: ["MYE "] },
  { key: "MYS", label: "MYS", prefixes: ["MYS "] },
  { key: "FK", label: "FK", prefixes: ["FK "] },
  { key: "DE", label: "DE", prefixes: ["MY DE"] },
];

// Which two counts each handover actually needs.
const FIELDS = {
  pickup: [
    { key: "pickup_qty", label: "Pickup" },
    { key: "cancel_qty", label: "Cancel" },
  ],
  return: [
    { key: "cour_r_qty", label: "Cour R" },
    { key: "cust_r_qty", label: "OBD" },
  ],
};

// A count of 0 is shown as an empty box — see the input below.
const blankZero = (v) =>
  (v === 0 || v === "0" || v === null || v === undefined) ? "" : String(v);

const isFk = (n) => /^FK\s/i.test(n || "");
const isMyntraOtc = (n) =>
  /^(MYS|MYE)\s+(HAMBERG|SLOWLORIS|OUTFIT|CRYPTIC|CALM|DIVINE)\b/i.test(n || "");

// "MYS CALM INCENSE" -> "CALM INCENSE";  "MY DE" -> "DE"
const brandOf = (n) => String(n || "").replace(/^(MYE|MYS|FK|MY)\s+/i, "").trim();

export default function PickupReturnPage({ selectedDate: dateProp, setSelectedDate: setDateProp }) {
  const [localDate, setLocalDate] = useState(getAppDateString());
  const selectedDate = dateProp ?? localDate;
  const setSelectedDate = setDateProp ?? setLocalDate;

  const [rows, setRows] = useState([]);          // prints rows for the day
  const [summary, setSummary] = useState([]);    // scanned/total per account
  const [group, setGroup] = useState("MY");
  const [brand, setBrand] = useState("");
  const [mode, setMode] = useState("pickup");
  const [saving, setSaving] = useState("");
  // Fields the user has typed in during this session. A 0 that came from the DB
  // is shown blank (nobody wants to clear it before every entry), but a 0 the
  // user just typed must stay on screen — otherwise it looks like it was lost.
  const [touched, setTouched] = useState({});
  const [retPick, setRetPick] = useState({ open: false, account: "" });
  const [nsOpen, setNsOpen] = useState(false);
  const [nsGroup, setNsGroup] = useState("");
  const [msg, setMsg] = useState("");

  // OTC popup (same shape as the Prints page)
  const [otc, setOtc] = useState({ open: false, account: "", loading: false, code: "", note: "" });
  const [elapsed, setElapsed] = useState(0);

  const load = useCallback(async (d) => {
    try {
      const { data } = await api.get(`/api/get-prints/${d}`);
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch {
      setRows([]);
    }
    try {
      const { data } = await api.get("/api/scan-summary", { params: { date: d } });
      setSummary(Array.isArray(data) ? data : []);
    } catch {
      setSummary([]);
    }
  }, []);

  useEffect(() => { setTouched({}); load(selectedDate); }, [selectedDate, load]);

  useEffect(() => {
    if (!otc.open || !otc.loading) return undefined;
    const t0 = Date.now();
    setElapsed(0);
    const t = setInterval(() => setElapsed((Date.now() - t0) / 1000), 100);
    return () => clearInterval(t);
  }, [otc.open, otc.loading, otc.account]);

  const inGroup = useCallback((name) => {
    const g = GROUPS.find((x) => x.key === group);
    if (!g) return false;
    return g.prefixes.some((p) => String(name || "").toUpperCase().startsWith(p));
  }, [group]);

  // Primary source is /api/get-prints, but a fresh day may briefly have no
  // prints rows while scan-summary already has orders. Keep the dropdown alive
  // from either source so it never collapses to "no accounts" for a valid day.
  const accountNames = useMemo(() => {
    const seen = [];
    const add = (name) => {
      const clean = String(name || "").trim();
      if (clean && !seen.includes(clean)) seen.push(clean);
    };
    rows.forEach((r) => add(r.account_name));
    summary.forEach((r) => add(r.account_name));
    return seen;
  }, [rows, summary]);

  // brands available in the chosen group, de-duplicated (MY shows each brand once)
  const brands = useMemo(() => {
    const seen = [];
    accountNames.filter(inGroup).forEach((n) => {
      const b = brandOf(n);
      if (b && !seen.includes(b)) seen.push(b);
    });
    return seen.sort();
  }, [accountNames, inGroup]);

  useEffect(() => {
    if (brands.length && !brands.includes(brand)) setBrand(brands[0]);
    if (!brands.length && brand) setBrand("");
  }, [brands, brand]);

  // the row(s) being shown — MY can legitimately be two (MYE + MYS)
  const shown = useMemo(() => {
    const rowByAccount = new Map(rows.map((r) => [r.account_name, r]));
    return accountNames
      .filter((name) => inGroup(name) && brandOf(name) === brand)
      .map((name) => rowByAccount.get(name) || ({
        account_name: name,
        date: selectedDate,
        pickup_qty: 0,
        cancel_qty: 0,
        cust_r_qty: 0,
        cour_r_qty: 0,
        pickup_otc: null,
        return_otc: null,
        remarks: "",
      }));
  }, [accountNames, rows, inGroup, brand, selectedDate]);

  // scanned / total / -remaining for an account, as on the scan progress view
  const detailOf = (account) => {
    const s0 = summary.find((x) => x.account_name === account) || {};
    const total = Number(s0.total_orders || 0);
    const scanned = Number(s0.scanned_entries || 0);
    return { total, scanned, remaining: Math.max(0, total - scanned) };
  };

  // Totals for a set of accounts, so a group or a brand can be judged at a glance
  // without opening it.
  const totalsFor = (matcher) => {
    let total = 0, scanned = 0;
    summary.forEach((x) => {
      if (!matcher(x.account_name)) return;
      total += Number(x.total_orders || 0);
      scanned += Number(x.scanned_entries || 0);
    });
    return { total, scanned, remaining: Math.max(0, total - scanned) };
  };

  const groupTotals = (key) => {
    const g = GROUPS.find((x) => x.key === key);
    if (!g) return { total: 0, scanned: 0, remaining: 0 };
    return totalsFor((n) => g.prefixes.some(
      (pre) => String(n || "").toUpperCase().startsWith(pre)));
  };

  const brandTotals = (b) =>
    totalsFor((n) => inGroup(n) && brandOf(n) === b);

  // scanned/total/-remaining, rendered the same way everywhere
  const Detail = ({ d, size = 12 }) => (
    <Typography component="span"
                sx={{ fontSize: size, fontFamily: "monospace", whiteSpace: "nowrap" }}>
      {d.scanned}/{d.total}/
      <b style={{ color: d.remaining > 0 ? "#d32f2f" : "#2e7d32" }}>-{d.remaining}</b>
    </Typography>
  );

  // The OTC is only offered once the counts are actually in, exactly as the
  // Prints page has always required — a code handed over while the numbers still
  // disagree is the thing that guard exists to prevent.
  const otcGate = (r) => {
    if (mode === "return") {
      // Flipkart is never gated here: START is given the moment the agent
      // arrives, before anything is counted, so blocking it would block the
      // handover. Its END is gated when END is actually chosen (see fetchOtc).
      if (isFk(r.account_name)) return { ok: true };
      // Myntra has ONE return code and no START, so the count rule applies to
      // the button directly.
      const cour = parseInt(r.cour_r_qty, 10) || 0;
      const obd = parseInt(r.cust_r_qty, 10) || 0;
      return cour > 0 || obd > 0
        ? { ok: true }
        : { ok: false, why: "Fill Cour R or OBD to get the Return OTC" };
    }
    const pick = parseInt(r.pickup_qty, 10) || 0;
    const canc = parseInt(r.cancel_qty, 10) || 0;
    const summaryRow = summary.find((x) => x.account_name === r.account_name) || {};
    const scannedQuantity = Number(summaryRow.scanned_quantity ?? summaryRow.scanned_entries ?? 0);
    if (scannedQuantity <= 0) {
      return {
        ok: false,
        why: "No scanned quantity found for this account/date",
      };
    }
    return pick + canc === scannedQuantity
      ? { ok: true }
      : {
        ok: false,
        why: `Pickup ${pick} + Cancel ${canc} = ${pick + canc}, needs ${scannedQuantity}`,
      };
  };

  const setQty = async (account, field, value) => {
    setTouched((t) => ({ ...t, [`${account}-${field}`]: true }));
    setRows((prev) => prev.map((r) =>
      r.account_name === account ? { ...r, [field]: value } : r));
    setSaving(`${account}-${field}`);
    try {
      await api.put("/api/update-qty", { accountName: account, [field]: value, date: selectedDate });
    } catch {
      setMsg("Could not save — check the connection");
    } finally {
      setSaving("");
    }
  };

  const fetchOtc = async (account, retMode) => {
    const kind = mode === "return" ? "return" : "pickup";
    const storedRow = shown.find((r) => r.account_name === account) || {};
    const storedPickupCode = String(storedRow.pickup_otc || "").trim();

    // Only Flipkart splits a return handover into two codes — START when the
    // agent arrives and END once he has finished scanning. Myntra issues a
    // single return code, so it must never be asked which one.
    if (kind === "return" && isFk(account) && !retMode) {
      setRetPick({ open: true, account });
      return;
    }

    setOtc({ open: true, account, loading: true, code: "", note: "" });
    const finish = (code, note) =>
      setOtc({ open: true, account, loading: false, code: code || "", note: note || "" });

    // Only END needs the counts — that is the code handed over after the returns
    // are tallied. START must stay available at any time.
    if (kind === "return" && isFk(account) && retMode === "end") {
      const row = shown.find((r) => r.account_name === account) || {};
      const cour = parseInt(row.cour_r_qty, 10) || 0;
      const obd = parseInt(row.cust_r_qty, 10) || 0;
      if (cour <= 0 && obd <= 0) { finish("", "Fill Cour R or OBD first"); return; }
    }

    try {
      // Only Flipkart Return OTC is tied to the operator's local Chrome
      // profiles. Pickup OTC and all Myntra OTC flows remain server-backed so
      // their stored values and existing worker state continue to work.
      if (kind === "return" && isFk(account)) {
        const body = kind === "return" ? { account, mode: retMode || "start" } : { account };
        const localApi = localFlipkartApi;
        const { data } = await localApi.post(`/api/flipkart-otc/${kind}`, body);
        const started = Date.now();
        const poll = setInterval(async () => {
          try {
            const { data: st } = await localApi.get(`/api/flipkart-otc/${kind}/${data.id}`);
            const box = kind === "return" ? st.returnOtc : st.pickupOtc;
            const row = box && (box.results || [])[0];
            if (st.done || row) {
              clearInterval(poll);
              const rawCode = row && (row.otc || row.codes);
              const liveCode = kind === "return"
                ? ((String(rawCode || "").match(/\d{4,}/) || [])[0] || "")
                : rawCode;
              const rowNote = row && (
                row.reason || (((row.fes || []).find((f) => f.note) || {}).note)
              );
              if (kind === "pickup" && !liveCode && storedPickupCode) {
                finish(storedPickupCode, "stored value");
              } else {
                finish(liveCode, row && !liveCode ? (rowNote || "no code") : "");
              }
            } else if (Date.now() - started > (retMode === "end" ? 135000 : 75000)) {
              clearInterval(poll);
              if (kind === "pickup" && storedPickupCode) finish(storedPickupCode, "stored value");
              else finish("", "timed out");
            }
          } catch { /* keep polling */ }
        }, 1000);
      } else if (isMyntraOtc(account)) {
        const { data } = await api.post(`/api/myntra-otc/${kind}`, { account });
        const started = Date.now();
        const poll = setInterval(async () => {
          try {
            const { data: st } = await api.get(
              `/api/myntra-otc/${kind}/${data.id}?channel=${encodeURIComponent(data.channel)}`);
            if (st.done) {
              clearInterval(poll);
              finish(st.code, st.code ? "" : (st.note || "no code"));
            } else if (Date.now() - started > 180000) {
              clearInterval(poll); finish("", "timed out (Myntra proxy slow)");
            }
          } catch (err) {
            clearInterval(poll);
            finish("", err?.response?.data?.error || "Myntra OTC status check failed");
          }
        }, 1000);
      } else {
        // no live source (e.g. MY DE) — show whatever was stored
        const row = shown.find((r) => r.account_name === account) || {};
        finish(kind === "return" ? row.return_otc : row.pickup_otc, "stored value");
      }
    } catch (err) {
      if (kind === "pickup" && isFk(account) && storedPickupCode) {
        finish(storedPickupCode, "stored value");
      } else {
        finish("", err?.response?.data?.error || "could not start the fetch");
      }
    }
  };

  const sel = { minWidth: 0, width: "100%", bgcolor: "#fff",
                "& .MuiSelect-select": { py: 1.2, fontWeight: 700 } };

  return (
    <Box sx={{ p: 1.5, maxWidth: 560, mx: "auto" }}>
      {/* date — top right, as on the other pages */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 20 }}>📦 Pickup / Return</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button size="small" sx={{ minWidth: 32, px: 0 }}
                  onClick={() => setSelectedDate(addDaysToDateString(selectedDate, -1))}>
            <ChevronLeftIcon fontSize="small" />
          </Button>
          <TextField type="date" size="small" value={selectedDate}
                     onChange={(e) => setSelectedDate(e.target.value)}
                     sx={{ width: 150, bgcolor: "#fff" }} />
          <Button size="small" sx={{ minWidth: 32, px: 0 }}
                  onClick={() => setSelectedDate(addDaysToDateString(selectedDate, 1))}>
            <ChevronRightIcon fontSize="small" />
          </Button>
        </Box>
      </Box>

      {/* the three selectors */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
        <Select size="small" value={group} sx={{ ...sel, gridColumn: "1 / -1" }}
                onChange={(e) => setGroup(e.target.value)}
                renderValue={(v) => {
                  const g = GROUPS.find((x) => x.key === v);
                  return (
                    <Box sx={{ display: "flex", justifyContent: "space-between",
                               alignItems: "baseline", gap: 1, pr: 1 }}>
                      <span>{g ? g.label : v}</span>
                      <Detail d={groupTotals(v)} size={12} />
                    </Box>
                  );
                }}>
          {GROUPS.map((g) => (
            <MenuItem key={g.key} value={g.key}>
              <Box sx={{ display: "flex", justifyContent: "space-between",
                         alignItems: "baseline", gap: 2, width: "100%" }}>
                <span>{g.label}</span>
                <Detail d={groupTotals(g.key)} size={12} />
              </Box>
            </MenuItem>
          ))}
        </Select>
        <Select size="small" value={brand} displayEmpty sx={sel}
                onChange={(e) => setBrand(e.target.value)}
                renderValue={(v) => (v ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between",
                             alignItems: "baseline", gap: 1, pr: 1 }}>
                    <span>{v}</span>
                    <Detail d={brandTotals(v)} size={11} />
                  </Box>
                ) : "no accounts")}>
          {brands.length === 0 && <MenuItem value="">no accounts</MenuItem>}
          {brands.map((b) => (
            <MenuItem key={b} value={b}>
              <Box sx={{ display: "flex", justifyContent: "space-between",
                         alignItems: "baseline", gap: 2, width: "100%" }}>
                <span>{b}</span>
                <Detail d={brandTotals(b)} size={11} />
              </Box>
            </MenuItem>
          ))}
        </Select>
        <Select size="small" value={mode} sx={sel} onChange={(e) => setMode(e.target.value)}>
          <MenuItem value="pickup">PICKUP</MenuItem>
          <MenuItem value="return">RETURN</MenuItem>
        </Select>
      </Box>

      {msg && <Typography sx={{ color: "error.main", fontSize: 13, mb: 1 }}>{msg}</Typography>}

      {/* one card per matching account — MY shows MYE and MYS together */}
      {shown.length === 0 ? (
        <Typography sx={{ opacity: 0.7, mt: 3, textAlign: "center" }}>
          Nothing for this selection on {selectedDate}.
        </Typography>
      ) : shown.map((r) => (
        <Box key={r.account_name}
             sx={{ border: "1px solid #d9d9d9", borderRadius: 2, p: 1.5, mb: 1.5, bgcolor: "#fafafa" }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
                     flexWrap: "wrap", gap: 1, mb: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{r.account_name}</Typography>
            <Detail d={detailOf(r.account_name)} size={13} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}>
            {FIELDS[mode].map((f) => (
              <Box key={f.key}>
                <Typography sx={{ fontSize: 12, opacity: 0.75, mb: 0.3 }}>{f.label}</Typography>
                <TextField
                  size="small" fullWidth type="text"
                  // A stored 0 shows as EMPTY: the box is a count someone still has
                  // to make, and pre-filling "0" meant deleting it before every
                  // entry. Typing 0 deliberately still saves 0.
                  value={touched[`${r.account_name}-${f.key}`]
                    ? (r[f.key] ?? "") : blankZero(r[f.key])}
                  onChange={(e) => setQty(r.account_name, f.key, e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="—"
                  sx={{ bgcolor: "#fff" }}
                  inputProps={{
                    // numeric keypad on phones (type=number would add spinners and
                    // let "e"/"-" through on desktop)
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    enterKeyHint: "done",
                    style: { textAlign: "center", fontSize: 20, fontWeight: 700, padding: 10 },
                  }}
                  helperText={saving === `${r.account_name}-${f.key}` ? "saving…" : " "}
                  FormHelperTextProps={{ sx: { m: 0, fontSize: 10, height: 12 } }}
                />
              </Box>
            ))}
          </Box>

          <TextField
            size="small" fullWidth multiline maxRows={3}
            label="Remarks"
            value={r.remarks ?? ""}
            onChange={(e) => setQty(r.account_name, "remarks", e.target.value)}
            sx={{ mt: 1.2, bgcolor: "#fff" }}
            inputProps={{ enterKeyHint: "done" }}
          />

          {(() => {
            const g = otcGate(r);
            return g.ok ? (
              <Button fullWidth variant="contained"
                      color={mode === "return" ? "warning" : "success"}
                      sx={{ mt: 1, py: 1.3, fontWeight: 800, fontSize: 16 }}
                      onClick={() => fetchOtc(r.account_name)}>
                {mode === "return" ? "RETURN OTC" : "PICKUP OTC"}
              </Button>
            ) : (
              // Not a disabled button: the code simply is not available yet, and
              // saying WHY is more use than a greyed-out control.
              <Typography sx={{ mt: 1, py: 1.1, textAlign: "center", fontSize: 13,
                                fontWeight: 700, color: "#8a6d00", bgcolor: "#fff6da",
                                border: "1px solid #f0d98a", borderRadius: 1 }}>
                {g.why}
              </Typography>
            );
          })()}
        </Box>
      ))}

      {/* the same Scan Progress panel the other pages carry, so the day's
          position is visible without leaving this screen */}
      <Box sx={{ mt: 2 }}>
        <ScanSummary
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          readOnlyDate={true}
          onOpenNotScanned={(g) => { setNsGroup(g || ""); setNsOpen(true); }}
        />
      </Box>
      {nsOpen && (
        <NotScannedDialog open={nsOpen} onClose={() => setNsOpen(false)}
                          accounts={[]} initialGroup={nsGroup} />
      )}

      {/* which return code? START on arrival, END after scanning */}
      <Modal open={retPick.open} onClose={() => setRetPick({ open: false, account: "" })}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%",
                   transform: "translate(-50%, -50%)", bgcolor: "#fff",
                   borderRadius: 2, boxShadow: 24, p: 2.5, width: "min(92vw, 340px)" }}>
          <div style={{ fontWeight: 800, fontSize: 17 }}>Return OTC</div>
          <div style={{ fontWeight: 700, margin: "4px 0 8px" }}>{retPick.account}</div>
          <div style={{ fontSize: 13, color: "#555", marginBottom: 14 }}>
            <b>START</b> — give on arrival.<br />
            <b>END</b> — after he finishes scanning (needs Cour R / OBD).
          </div>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button fullWidth variant="contained" color="success"
                    sx={{ py: 1.4, fontWeight: 800 }}
                    onClick={() => { const a = retPick.account;
                                     setRetPick({ open: false, account: "" });
                                     fetchOtc(a, "start"); }}>START</Button>
            <Button fullWidth variant="contained" color="warning"
                    sx={{ py: 1.4, fontWeight: 800 }}
                    onClick={() => { const a = retPick.account;
                                     setRetPick({ open: false, account: "" });
                                     fetchOtc(a, "end"); }}>END</Button>
          </Box>
        </Box>
      </Modal>

      {/* the code, big enough to read across the table */}
      <Modal open={otc.open} onClose={() => setOtc({ ...otc, open: false })}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%",
                   transform: "translate(-50%, -50%)", bgcolor: "#0b3d2e", color: "#fff",
                   borderRadius: 2, boxShadow: 24, p: 2.5,
                   width: "min(92vw, 320px)", textAlign: "center" }}>
          <div style={{ position: "absolute", top: 6, right: 10, fontSize: 11,
                        fontFamily: "monospace", opacity: otc.loading ? 0.9 : 0.55,
                        color: otc.loading ? "#8fd6b4" : "#cfe8db" }}>
            {elapsed.toFixed(1)}s
          </div>
          <div style={{ letterSpacing: 2, opacity: 0.85, fontSize: 12 }}>
            {mode === "return" ? "RETURN OTC" : "PICKUP OTC"}
          </div>
          <div style={{ fontWeight: 700, margin: "6px 0 10px" }}>{otc.account}</div>
          {otc.loading ? (
            <div style={{ fontSize: 18, padding: "14px 0" }}>fetching…</div>
          ) : otc.code ? (
            <div style={{ fontFamily: "monospace", fontWeight: 800,
                          fontSize: "clamp(40px, 14vw, 60px)", letterSpacing: 6,
                          lineHeight: 1.1, userSelect: "all", cursor: "pointer" }}
                 title="Click to copy"
                 onClick={() => { try { navigator.clipboard.writeText(otc.code); } catch { /* */ } }}>
              {otc.code}
            </div>
          ) : (
            <div style={{ fontSize: 15, fontWeight: 700, color: "#ffd479", padding: "8px 0" }}>
              {otc.note || "no code"}
            </div>
          )}
          <Button size="small" variant="contained" sx={{ mt: 1.2 }}
                  onClick={() => setOtc({ ...otc, open: false })}>
            Close
          </Button>
        </Box>
      </Modal>
    </Box>
  );
}
